"use client";

import React, { useState } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@perceptron/ui/components/dialog";
import { Textarea } from "@perceptron/ui/components/textarea";
import { formatString } from "@perceptron/utils/string";
import { toast } from "sonner";

import { submitReport } from "@demo/libs/api/reportApi";
import { capturePageScreenshot, SCREENSHOT_HIDE_ATTR } from "@demo/libs/screenshot";
import type { ReportPayload } from "@demo/server/api/contracts/report";

const MAX_LENGTH = 2000;

type SubmitState = "idle" | "capturing" | "submitting";

interface ReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Assemble the failure payload for the typed message; called at submit time. */
  buildPayload: (message: string) => ReportPayload;
  /** Fires after a successful submit (not on cancel). */
  onSubmitted?: () => void;
}

export const ReportDialog: React.FC<ReportDialogProps> = ({ open, onOpenChange, buildPayload, onSubmitted }) => {
  const [message, setMessage] = useState("");
  const [state, setState] = useState<SubmitState>("idle");

  const handleSubmit = async () => {
    if (!message.trim() || state !== "idle") return;
    setState("capturing");

    const screenshot = await capturePageScreenshot();
    setState("submitting");

    const payload = buildPayload(message.trim());

    try {
      const result = await submitReport({ payload, screenshot });
      if (result.partial) {
        toast.success(formatString(strings.report.toasts.sentPartial, result.failed.length));
      } else if (!screenshot) {
        toast.success(strings.report.toasts.sentNoScreenshot);
      } else {
        toast.success(strings.report.toasts.sent);
      }
      setMessage("");
      onSubmitted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : strings.report.toasts.failed);
    } finally {
      setState("idle");
    }
  };

  // The dialog itself is excluded from the screenshot so the modal does not
  // appear in the captured image.
  const dialogProps = { [SCREENSHOT_HIDE_ATTR]: "true" } as Record<string, string>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent {...dialogProps}>
        <DialogHeader>
          <DialogTitle>{strings.report.dialog.title}</DialogTitle>
          <DialogDescription>{strings.report.dialog.description}</DialogDescription>
        </DialogHeader>
        <Textarea
          autoFocus
          maxLength={MAX_LENGTH}
          placeholder={strings.report.dialog.placeholder}
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
        />
        <DialogFooter>
          <Button disabled={state !== "idle"} variant="ghost" onClick={() => onOpenChange(false)}>
            {strings.report.dialog.cancel}
          </Button>
          <Button disabled={!message.trim() || state !== "idle"} onClick={handleSubmit}>
            {state === "capturing"
              ? strings.report.dialog.capturing
              : state === "submitting"
                ? strings.report.dialog.sending
                : strings.report.dialog.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
